import { useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { Link2 } from "lucide-react";
import { Button, EmptyState, Pagination } from "../../components/ui";
import {
  CustomUrlListToolbar,
  type ToolbarFilters,
  type StatusTab,
} from "./custom-url-list/CustomUrlListToolbar";
import {
  CustomUrlRow,
  type CustomUrlRowData,
} from "./custom-url-list/CustomUrlRow";

const PER_PAGE = 15;

const ALL_CUSTOM_URLS = gql`
  query AllCustomUrlsForListing(
    $page: Int!
    $perPage: Int!
    $filter: CustomUrlFilter
  ) {
    allCustomUrls(
      page: $page
      perPage: $perPage
      sortField: "updatedAt"
      sortOrder: "DESC"
      filter: $filter
    ) {
      id
      tenantId
      slug
      targetType
      targetLandingPageId
      targetPath
      isActive
      updatedAt
      createdAt
    }
    _allCustomUrlsMeta(filter: $filter) {
      count
    }
  }
`;

const CUSTOM_URL_STATS = gql`
  query CustomUrlStats($ids: [String]!) {
    customUrlStats(ids: $ids) {
      customUrlId
      clicks
      conversions
    }
  }
`;

interface QueryData {
  allCustomUrls: CustomUrlRowData[];
  _allCustomUrlsMeta: { count: number };
}

const INITIAL_FILTERS: ToolbarFilters = {
  search: "",
  status: "all",
};

export function CustomUrlsListing() {
  const [filters, setFilters] = useState<ToolbarFilters>(INITIAL_FILTERS);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [filters.search, filters.status]);

  const filterVar = useMemo(
    () => buildFilter(filters),
    [filters.search, filters.status],
  );

  const { data, previousData, loading, error, refetch } =
    useQuery<QueryData>(ALL_CUSTOM_URLS, {
      variables: { page, perPage: PER_PAGE, filter: filterVar },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    });

  const stable = data ?? previousData;
  const urls = stable?.allCustomUrls ?? [];

  // Fetch stats for visible URLs
  const { data: statsData, loading: statsLoading } = useQuery(
    CUSTOM_URL_STATS,
    {
      skip: !urls.length,
      variables: { ids: urls.map((u) => u.id) },
      fetchPolicy: "cache-and-network",
    }
  );

  // Build a map of custom URL ID to stats
  const statsMap = useMemo(() => {
    const map = new Map<string, { clicks: number; conversions: number }>();
    if (statsData?.customUrlStats) {
      for (const stat of statsData.customUrlStats) {
        map.set(stat.customUrlId, {
          clicks: stat.clicks,
          conversions: stat.conversions,
        });
      }
    }
    return map;
  }, [statsData]);

  const total = stable?._allCustomUrlsMeta?.count ?? 0;
  const initialLoad = loading && !stable;

  const filtersActive =
    filters.search.trim().length > 0 || filters.status !== "all";

  if (error && !stable) {
    return (
      <div className="grid grid-cols-12 gap-3">
        <section className="shadow-card col-span-12 rounded-lg bg-paper-hi p-8 text-center">
          <div className="mb-1 text-[14px] font-medium">
            Couldn't load custom URLs
          </div>
          <div className="mb-4 text-[12.5px] text-ink-4">{error.message}</div>
          <Button variant="primary" onClick={() => refetch()}>
            Try again
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-3">
      <section className="shadow-card col-span-12 overflow-hidden rounded-lg bg-paper-hi">
        <div className="hair-b border-b flex items-center justify-between gap-3 px-5 py-4">
          <div>
            <h2 className="text-[15px] font-semibold">Custom URLs</h2>
            <p className="mt-0.5 text-[12px] text-ink-4">
              {filtersActive
                ? `${total} matching ${total === 1 ? "URL" : "URLs"}`
                : `${total} total ${total === 1 ? "URL" : "URLs"}`}
            </p>
          </div>
          <CustomUrlListToolbar filters={filters} onFiltersChange={setFilters} />
        </div>

        {initialLoad ? (
          <RowSkeletons />
        ) : urls.length === 0 ? (
          filtersActive ? (
            <EmptyState
              icon={<Link2 size={28} strokeWidth={1.5} />}
              heading="No matching URLs"
              description="Try a different search term or clear the filter."
            />
          ) : (
            <EmptyState
              icon={<Link2 size={28} strokeWidth={1.5} />}
              heading="No custom URLs yet"
              description="Create a custom URL to give out short, branded links for ads, QR codes, or business cards."
            />
          )
        ) : (
          <div
            className={`divide-hair ${loading ? "opacity-60" : ""} transition-opacity`}
          >
            {urls.map((u) => (
              <CustomUrlRow key={u.id} url={u} stats={statsMap.get(u.id)} statsLoading={statsLoading} />
            ))}
          </div>
        )}

        {!initialLoad && total > 0 && (
          <div className="hair-t border-t bg-canvas/40 px-5 py-3">
            <Pagination
              page={page}
              perPage={PER_PAGE}
              total={total}
              onPageChange={setPage}
              unit="URL"
            />
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Translate the toolbar's UI state into the CustomUrlFilter input the
 * server expects. Returns undefined when no filters are active so the
 * filter argument stays absent on the wire.
 */
function buildFilter(filters: ToolbarFilters):
  | {
      status?: string;
      q?: string;
    }
  | undefined {
  const search = filters.search.trim();
  const tabStatus = mapTabToStatus(filters.status);
  if (!search && !tabStatus) return undefined;
  return {
    ...(tabStatus ? { status: tabStatus } : {}),
    ...(search ? { q: search } : {}),
  };
}

function mapTabToStatus(tab: StatusTab): string | null {
  switch (tab) {
    case "active":
      return "active";
    case "inactive":
      return "inactive";
    default:
      return null;
  }
}

function RowSkeletons() {
  return (
    <div className="divide-hair">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="pulse-soft h-3 w-1/4 rounded bg-canvas-2" />
            <div className="pulse-soft h-4 w-3/4 rounded bg-canvas-2" />
            <div className="pulse-soft h-3 w-2/3 rounded bg-canvas-2" />
          </div>
        </div>
      ))}
    </div>
  );
}
